import { createContext, useState, useMemo } from "react";
import { createTheme } from "@mui/material/styles";

// color design tokens export
export const tokens = (mode) => ({
  ...(mode === "dark"
    ? {
      grey: {
        100: "#ECECF1",
        200: "#D1D5DB",
        300: "#9CA3AF",
        400: "#6B7280",
        500: "#4B5563",
        600: "#374151",
        700: "#1F2937",
        800: "#111827",
        900: "#030712",
      },
      primary: {
        100: "#d0d1d5",
        200: "#a1a4ab",
        300: "#727681",
        400: "#1F2A40",
        500: "#141b2d",// #222831//#141b2d//#26273b
        600: "#101624",
        700: "#0c101b",
        800: "#080b12",
        900: "#040509",
      },

      greenAccent: {
        100: "#dbf5ee",
        200: "#b7ebde",
        300: "#94e2cd",
        400: "#70d8bd",
        500: "#4cceac",
        600: "#3da58a",
        700: "#2e7c67",
        800: "#1e5245",
        900: "#0f2922",
      },
      redAccent: {
        100: "#f8dcdb",
        200: "#f1b9b7",
        300: "#e99592",
        400: "#e2726e",
        500: "#db4f4a",
        600: "#af3f3b",
        700: "#832f2c",
        800: "#58201e",
        900: "#2c100f",
      },
      blueAccent: {
        50: "#e3f2fd",
        100: "#bbdefb",
        200: "#90caf9",
        300: "#64b5f6",
        400: "#42a5f5",
        500: "#2196f3",
        600: "#1e88e5",
        700: "#1976d2",
        800: "#1565c0",
        900: "#0d47a1",
      },
      orangeAccent: {
        100: "#ffe5d1",
        200: "#ffc7a3",
        300: "#ffa874",
        400: "#ff8a46",
        500: "#ff6b18",
        600: "#cc5613",
        700: "#99400e",
        800: "#662b0a",
        900: "#331505",
      },

      purpleAccent: {
        100: "#e8dff8",
        200: "#d1bff1",
        300: "#b99fea",
        400: "#a27fe3",
        500: "#8b5fdd",
        600: "#6f4cb1",
        700: "#533985",
        800: "#382659",
        900: "#1c132c",
      },

      yellowAccent: {
        100: "#fff7cc",
        200: "#ffef99",
        300: "#ffe766",
        400: "#ffde33",
        500: "#ffd600",
        600: "#ccab00",
        700: "#997f00",
        800: "#665400",
        900: "#332a00",
      },
    }
    : {
      grey: {
        100: "#141414",
        200: "#292929",
        300: "#3d3d3d",
        400: "#525252",
        500: "#666666",
        600: "#858585",
        700: "#a3a3a3",
        800: "#c2c2c2",
        900: "#e0e0e0",
      },
      primary: {
        // 100: "#040509",
        // 200: "#080b12",
        // 300: "#0c101b",
        // 400: "#fefefeff", // manually changed
        // 500: "#759fb5ff",
        // 600: "#1F2A40",
        // 700: "#727681",
        // 800: "#a1a4ab",
        // 900: "#d0d1d5",
        100: "#F7F9FA", // App background (SAP-like off-white)
        200: "#EEF1F3", // Page background
        300: "#E3E7EA", // Borders / dividers
        400: "#FFFFFF", // Cards / paper (pure white only here)
        500: "#e5f0f9ff", // Primary SAP blue accent
        600: "#354A5F", // Primary text / headers (SAP standard)
        700: "#6A6D70", // Secondary text
        800: "#9FA3A7", // Disabled text / icons
        900: "#D5DADD"  // Subtle separators / hover background
      },

      greenAccent: {
        100: "#0f2922",
        200: "#1e5245",
        300: "#2e7c67",
        400: "#3da58a",
        500: "#4cceac",
        600: "#70d8bd",
        700: "#94e2cd",
        800: "#b7ebde",
        900: "#dbf5ee",
      },
      redAccent: {
        100: "#2c100f",
        200: "#58201e",
        300: "#832f2c",
        400: "#af3f3b",
        500: "#db4f4a",
        600: "#e2726e",
        700: "#e99592",
        800: "#f1b9b7",
        900: "#f8dcdb",
      },
      blueAccent: {
        50: "#e3f2fd",
        100: "#bbdefb",
        200: "#90caf9",
        300: "#64b5f6",
        400: "#42a5f5",
        500: "#2196f3",
        600: "#1e88e5",
        700: "#1976d2",
        800: "#1565c0",
        900: "#0d47a1",
      },
      orangeAccent: {
        100: "#331505",
        200: "#662b0a",
        300: "#99400e",
        400: "#cc5613",
        500: "#ff6b18",
        600: "#ff8a46",
        700: "#ffa874",
        800: "#ffc7a3",
        900: "#ffe5d1",
      },

      purpleAccent: {
        100: "#1c132c",
        200: "#382659",
        300: "#533985",
        400: "#6f4cb1",
        500: "#8b5fdd",
        600: "#a27fe3",
        700: "#b99fea",
        800: "#d1bff1",
        900: "#e8dff8",
      },

      yellowAccent: {
        100: "#332a00",
        200: "#665400",
        300: "#997f00",
        400: "#ccab00",
        500: "#ffd600",
        600: "#ffde33",
        700: "#ffe766",
        800: "#ffef99",
        900: "#fff7cc",
      },

    }),
});



// mui theme settings
export const themeSettings = (mode) => {
  const colors = tokens(mode);
  return {
    palette: {
      mode: mode,
      ...(mode === "dark"
        ? {
          // palette values for dark mode
          primary: {
            main: colors.primary[700],
            text: colors.grey[100],
          },
          secondary: {
            main: colors.blueAccent[500],

          },
          neutral: {
            dark: colors.grey[500],
            main: colors.grey[500],
            light: colors.grey[100],
          },
          background: {
            default: colors.primary[500],
          },
        }
        : {
          // palette values for light mode
          primary: {
            main: colors.primary[100],
            text: colors.grey[900],
          },
          secondary: {
            main: colors.blueAccent[700],
          },
          neutral: {
            dark: colors.grey[700],
            main: colors.grey[500],
            light: colors.grey[100],
          },
          background: {
            default: "#fff",
          },
        }),
    },

    typography: {
      fontFamily: ["Cambria", "serif"].join(","),
      fontSize: 14,
      h1: {
        fontFamily: "inherit",
        fontSize: 36,
        fontWeight: 600,
      },
      h2: {
        fontFamily: "inherit",
        fontSize: 30,
        fontWeight: 600,
      },
      h3: {
        fontFamily: "inherit",
        fontSize: 24,
        fontWeight: 500,
      },
      h4: {
        fontFamily: "inherit",
        fontSize: 20,
        fontWeight: 500,
      },
      h5: {
        fontFamily: "inherit",
        fontSize: 16,
        fontWeight: 500,
      },
      h6: {
        fontFamily: "inherit",
        fontSize: 14,
        fontWeight: 500,
      },
      body1: {
        fontFamily: "inherit",
        fontSize: 14,
      },
      body2: {
        fontFamily: "inherit",
        fontSize: 12,
      },
      button: {
        fontFamily: "inherit",
        fontSize: 12,
        textTransform: "none",
        fontWeight: 600,
      },
      caption: {
        fontFamily: "inherit",
        fontSize: 12,
        color: "#666",
      },
    },


    components: {
      MuiPaper: {
        styleOverrides: {
          root: {
            borderRadius: 10,
            boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: "none",
            borderRadius: 10,
            fontSize: "10px",
          },
        },
      },
      MuiListItemButton: {
        styleOverrides: {
          root: {
            "&.Mui-selected": {
              backgroundColor: "#e3f2fd", // or use tokens in here if possible
              "& .MuiListItemIcon-root": {
                color: "#1976d2",
              },
            },
          },
        },
      },
    },
  };
};

// context for color mode
export const ColorModeContext = createContext({ toggleColorMode: () => { } });

export const useMode = () => {
  const [mode, setMode] = useState("dark");

  const colorMode = useMemo(
    () => ({
      toggleColorMode: () =>
        setMode((prev) => (prev === "light" ? "dark" : "light")),
    }),
    []
  );

  const theme = useMemo(() => createTheme(themeSettings(mode)), [mode]);
  return [theme, colorMode];
};
